import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import user_image from "../../images/user_img.jpg";
import { Table, message, Button, Tooltip, Modal } from "antd";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { getScoreBreakdown } from "../../services/scoreBreakdown";
import { getTestAssignmentDetails } from "../../services/testAssignments";
import { getAntiCheatReport, type AntiCheatReport } from "../../services/antiCheat";
import AntiCheatModal from "../../components/AntiCheatModal";
import AntiCheatWarning from "../../components/AntiCheatWarning";
import api from "../../services/api";
import BackButton from "../../components/BackButton";

ChartJS.register(ArcElement, ChartTooltip, Legend);

interface TestRow {
  type: string;
  result_type: string;
  result: number;
}

interface AssignmentData {
  id: number;
  user: {
    first_name: string;
    last_name: string;
    email: string;
    profile_image?: string;
  };
  test: {
    name: string;
    time_limit: number;
    question_count: number;
    industry?: string;
  };
  completed_at: string;
  started_at: string;
  score: number;
  total_questions: number;
  correct_answers: number;
  time_spent: number; // in minutes
  current_user_invited_candidate: boolean; // Whether current user invited this candidate
}

const CandidateTestResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const assignmentId = searchParams.get("assignment_id");

  const [loading, setLoading] = useState(true);
  const [rows_Tests, setrows_Tests] = useState<TestRow[]>([]);
  const [assignmentData, setAssignmentData] = useState<AssignmentData | null>(null);
  const [hourlyRate, setHourlyRate] = useState<number>(0); // Start with 0 to guide user input

  // Authorization state
  const [accessAllowed, setAccessAllowed] = useState<boolean | null>(null);
  const [authorizationError, setAuthorizationError] = useState<{
    title: string;
    message: string;
    show_back_button: boolean;
  } | null>(null);

  // Anti-cheat state
  const [antiCheatReport, setAntiCheatReport] = useState<AntiCheatReport | null>(null);
  const [antiCheatModalVisible, setAntiCheatModalVisible] = useState(false);
  const [antiCheatWarningDismissed, setAntiCheatWarningDismissed] = useState(false);
  const [antiCheatLoading, setAntiCheatLoading] = useState(false);

  // Scoring-explainer modal state
  const [scoreInfoOpen, setScoreInfoOpen] = useState(false);

  // Check authorization before loading data
  const checkAuthorization = async (): Promise<boolean> => {
    if (!assignmentId) {
      setAuthorizationError({
        title: 'Invalid Request',
        message: 'No assignment ID provided. Please check your link.',
        show_back_button: true
      });
      setAccessAllowed(false);
      return false;
    }

    try {
      const response = await api.get(`assignment/${assignmentId}/test-results-access/`);

      if (response.data.access_allowed) {
        setAccessAllowed(true);
        setAuthorizationError(null);
        return true;
      } else {
        // Access denied
        setAccessAllowed(false);
        setAuthorizationError({
          title: response.data.title || 'Access Denied',
          message: response.data.message || 'You do not have permission to access these test results.',
          show_back_button: response.data.show_back_button !== false
        });
        return false;
      }
    } catch (error) {
      console.error('Authorization check failed:', error);
      setAccessAllowed(false);
      setAuthorizationError({
        title: 'Error',
        message: 'Unable to verify access permissions. Please try again.',
        show_back_button: true
      });
      return false;
    }
  };


  // Calculate derived data
  // Keep 1 decimal place of precision, matching the PDF report.
  const correctPercentage = assignmentData ? Math.round((assignmentData.correct_answers / assignmentData.total_questions) * 1000) / 10 : 0;
  const incorrectPercentage = assignmentData ? Math.round((100 - correctPercentage) * 10) / 10 : 0;
  const userName = assignmentData ? `${assignmentData.user.first_name} ${assignmentData.user.last_name}`.trim() || assignmentData.user.email : user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}`.trim() : user?.email || "User";
  const testDate = assignmentData ? new Date(assignmentData.completed_at).toLocaleDateString('en-AU') : "--";
  const timeSpent = assignmentData ? `${assignmentData.time_spent} minutes` : "--";

  // Training cost calculations
  const maxTrainingHours = 50;
  const trainingHours = Math.round(maxTrainingHours * (1 - correctPercentage / 100));
  const trainingCost = trainingHours * hourlyRate;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleHourlyRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
    // Ensure reasonable bounds for hourly rate
    if (value <= 1000) {
      setHourlyRate(value);
    }
  };

  // Achievement badge logic based on score percentiles
  const getAchievementBadge = (score: number) => {
    if (score >= 90) {
      return {
        text: "Highly Proficient",
        icon: (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" fill="#10B981" />
            <path d="M12 18L16 22L24 14" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
        bgColor: "#ECFDF5",
        textColor: "#047857",
        borderColor: "#10B981"
      };
    } else if (score >= 75) {
      return {
        text: "Proficient",
        icon: (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" fill="#3B82F6" />
            <path d="M18 8L20.4 13.2L26 14L21 19L22.8 25L18 22L13.2 25L15 19L10 14L15.6 13.2L18 8Z" fill="white" />
          </svg>
        ),
        bgColor: "#EFF6FF",
        textColor: "#1D4ED8",
        borderColor: "#3B82F6"
      };
    } else if (score >= 60) {
      return {
        text: "Emerging Skills",
        icon: (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" fill="#F59E0B" />
            <path d="M18 12V18L22 20" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="18" cy="18" r="9" stroke="white" strokeWidth="3" />
          </svg>
        ),
        bgColor: "#FFFBEB",
        textColor: "#D97706",
        borderColor: "#F59E0B"
      };
    } else {
      return {
        text: "Needs Development",
        icon: (
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" fill="#EF4444" />
            <path d="M18 10V18" stroke="white" strokeWidth="3" strokeLinecap="round" />
            <circle cx="18" cy="24" r="2" fill="white" />
          </svg>
        ),
        bgColor: "#FEF2F2",
        textColor: "#DC2626",
        borderColor: "#EF4444"
      };
    }
  };

  const achievementBadge = getAchievementBadge(assignmentData?.score ?? 0);

  const pieChartData = {
    datasets: [
      {
        data: [correctPercentage, incorrectPercentage],
        backgroundColor: ["#1890ff", "#ffa940"],
        borderWidth: 0,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  const columns_Tests = [
    {
      title: <span style={{ color: '#A3A6B7', fontWeight: 700, fontSize: 11 }}>Skills</span>,
      dataIndex: "type",
      key: "type",
      width: "35%",
      render: (_: string, row: TestRow) => (
        <span style={{ fontWeight: 700, fontSize: 14, color: '#23255A', fontFamily: 'inherit', letterSpacing: 0 }}>{row.type}</span>
      ),
    },
    {
      title: <span style={{ color: '#A3A6B7', fontWeight: 700, fontSize: 11 }}>Performance</span>,
      dataIndex: "result_type",
      key: "result_type",
      width: "20%",
      render: (_: string, row: TestRow) => {
        let iconElement = null;
        let textColor = '#23255A';

        if (row.result_type === "Highly Proficient") {
          iconElement = (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6 }} xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="10" fill="#10B981" />
              <path d="M6 10L9 13L14 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          );
          textColor = '#047857';
        } else if (row.result_type === "Proficient") {
          iconElement = (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6 }} xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="10" fill="#3B82F6" />
              <path d="M10 4L11.5 7.5L15 8L12 11L13 15L10 13L7 15L8 11L5 8L8.5 7.5L10 4Z" fill="white" />
            </svg>
          );
          textColor = '#1D4ED8';
        } else if (row.result_type === "Emerging Skills") {
          iconElement = (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6 }} xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="10" fill="#F59E0B" />
              <path d="M10 6V10L13 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="10" r="5" stroke="white" strokeWidth="2" />
            </svg>
          );
          textColor = '#D97706';
        } else if (row.result_type === "Needs Development") {
          iconElement = (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginRight: 6 }} xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="10" fill="#EF4444" />
              <path d="M10 5V10" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <circle cx="10" cy="13" r="1" fill="white" />
            </svg>
          );
          textColor = '#DC2626';
        }

        return (
          <span style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 12, color: textColor, fontFamily: 'inherit' }}>
            {iconElement}
            <span>{row.result_type}</span>
          </span>
        );
      },
    },
    {
      title: <span style={{ color: '#A3A6B7', fontWeight: 700, fontSize: 11 }}>Assessment</span>,
      dataIndex: "result",
      key: "result",
      width: "45%",
      render: (_: string, row: TestRow) => {
        const percent = typeof row.result === 'number' ? row.result : 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: '#23255A', minWidth: 56 }}>{percent.toFixed(1)}%</span>
            <div style={{ flex: 1, height: 8, background: '#F2F4F7', borderRadius: 8, overflow: 'hidden', minWidth: 120 }}>
              <div style={{ height: '100%', width: `${percent}%`, background: '#0066FF', borderRadius: 8, transition: 'width 0.3s' }}></div>
            </div>
          </div>
        );
      },
    },
  ];

  // Fetch assignment data using axios api instance
  const fetchAssignmentData = async () => {
    if (!assignmentId) {
      toast.error("Assignment ID is required");
      setLoading(false);
      return;
    }

    try {
      // Check authorization first
      const isAuthorized = await checkAuthorization();
      if (!isAuthorized) {
        setLoading(false);
        return;
      }

      // Fetch score breakdown for accounting types using service
      const scoreData = await getScoreBreakdown(assignmentId, "assignment");

      // Convert breakdown to table format
      const breakdownTests: TestRow[] = Object.entries(scoreData.score_breakdown || {}).map(([skill, score]) => {
        const scoreValue = score as number;
        let result_type = "Needs Development";
        if (scoreValue >= 90) {
          result_type = "Highly Proficient";
        } else if (scoreValue >= 75) {
          result_type = "Proficient";
        } else if (scoreValue >= 60) {
          result_type = "Emerging Skills";
        }
        return {
          type: skill,
          result_type,
          result: Math.round(scoreValue)
        };
      });
      setrows_Tests(breakdownTests);

      // Fetch assignment details using service
      const details = await getTestAssignmentDetails(assignmentId);
      setAssignmentData(details);
    } catch (error) {
      console.error("Error fetching assignment data:", error);
      toast.error("Failed to load assessment results");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDP = () => {
    if (assignmentId) {
      navigate(`/candidate/ppp?assignment_id=${assignmentId}`);
    }
  };

  const handleDownloadPDF = async () => {
    if (!assignmentId) {
      message.error('Assignment ID is required');
      return;
    }

    try {
      // Call backend PDF generation endpoint
      const response = await api.get(`assignment/${assignmentId}/generate-pdf/`, {
        responseType: 'blob' // Important: Tell axios to expect binary data
      });

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'test_results.pdf';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else if (assignmentData) {
        // Fallback to constructing filename from assignment data
        const safeName = `${assignmentData.user.first_name}_${assignmentData.user.last_name}`.replace(/[^a-zA-Z0-9]/g, '_');
        filename = `${safeName}_test_results.pdf`;
      }

      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to generate PDF. Please try again.');
    }
  };

  const handleDownloadCertificate = async () => {
    if (!assignmentId) {
      message.error('Assignment ID is required');
      return;
    }
    try {
      const response = await api.get(`assignment/${assignmentId}/certificate.pdf/`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'TestInvest_Certificate.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Certificate downloaded successfully!');
    } catch (error: any) {
      console.error('Error downloading certificate:', error);
      const detail = error?.response?.data?.error;
      message.error(detail || 'Failed to download certificate. Please try again.');
    }
  };

  // Anti-cheat functions
  const fetchAntiCheatReport = async () => {
    if (!assignmentId || !shouldShowAntiCheat) return;

    try {
      setAntiCheatLoading(true);
      const report = await getAntiCheatReport(parseInt(assignmentId));
      setAntiCheatReport(report);
    } catch (error) {
      console.error("Error fetching anti-cheat report:", error);
      // Silently fail - anti-cheat is not critical for page functionality
    } finally {
      setAntiCheatLoading(false);
    }
  };

  const handleViewAntiCheatDetails = () => {
    setAntiCheatModalVisible(true);
  };

  const handleCloseAntiCheatModal = () => {
    setAntiCheatModalVisible(false);
  };

  const handleDismissAntiCheatWarning = () => {
    setAntiCheatWarningDismissed(true);
  };

  useEffect(() => {
    fetchAssignmentData();
    // eslint-disable-next-line
  }, [assignmentId]);

  useEffect(() => {
    // Fetch anti-cheat report after assignment data is loaded
    if (assignmentData && assignmentId) {
      fetchAntiCheatReport();
    }
    // eslint-disable-next-line
  }, [assignmentData, assignmentId]);

  // Determine user type and permissions
  const isAdmin = !!user?.is_admin;
  const isSuperAdmin = !!user?.is_super_admin;
  const isCandidate = !isAdmin;
  const isInvitingBusinessUser = assignmentData?.current_user_invited_candidate || false;

  // Only show anti-cheat info to super admins or the business user who invited the candidate
  const shouldShowAntiCheat = (isSuperAdmin || isInvitingBusinessUser) && !isCandidate;

  return (
    <>
      <style>{`
        @media (min-width: 1200px) {
          .test-result-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 0 40px;
          }
        }
        @media (min-width: 1600px) {
          .test-result-container {
            max-width: 1600px;
            padding: 0 80px;
          }
        }
        @media (max-width: 1199px) {
          .test-result-container {
            padding: 0 20px;
          }
        }

		.test-skills-title span {
			font-family: "Sofia Pro Black", "Proxima Nova Black", "Proxima Nova Bold", sans-serif
		}
      `}</style>

      <BackButton />

      {/* Show error if access is denied */}
      {accessAllowed === false && authorizationError && (
        <div className="main-content" style={{ marginLeft: '8rem', marginRight: '8rem' }}>
          <div className="content-header" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{
                fontSize: '4rem',
                color: '#f44336',
                marginBottom: '20px'
              }}>🚫</div>
              <h2 style={{ color: '#333', marginBottom: '20px' }}>{authorizationError.title}</h2>
              <p style={{
                fontSize: '16px',
                color: '#666',
                lineHeight: '1.6',
                marginBottom: '30px'
              }}>
                {authorizationError.message}
              </p>
              {authorizationError.show_back_button && (
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                >
                  Go Back
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show loading state while checking access */}
      {accessAllowed === null && (
        <div className="main-content" style={{ marginLeft: '8rem', marginRight: '8rem' }}>
          <div className="content-header" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Checking access...</span>
            </div>
            <div style={{ marginTop: '20px', color: '#666' }}>Verifying access permissions...</div>
          </div>
        </div>
      )}

      {/* Show content if authorized */}
      {accessAllowed === true && isCandidate && (
        <div className="test-result-container">
          <div className="content-header d-flex justify-content-between align-items-center">
            <h3><strong>"{assignmentData?.test.name}" results</strong></h3>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                style={{ marginRight: '10px' }}
                onClick={handleViewPDP}
                disabled={!assignmentId}
              >
                View PPP
              </button>
              <button
                className="btn btn-primary"
                style={{ marginRight: '10px' }}
                onClick={handleDownloadPDF}
                disabled={!assignmentId}
              >
                Download Assessment PDF
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDownloadCertificate}
                disabled={!assignmentId}
              >
                Download Certificate
              </button>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="section-content layout" id="candidate-test-result-content" style={{
              margin: '0 auto',
            }}>
              <div className="mb-3">
                {/* First Row */}
                <div className="row mb-4 mt-3">
                  {/* Left Column - Profile Card */}
                  <div className="col-4">
                    <div className="card h-100">
                      <div className="card-body d-flex flex-column justify-content-center align-items-center text-center" style={{ padding: '48px 24px' }}>
                        <img
                          src={assignmentData?.user?.profile_image && assignmentData.user.profile_image.trim() !== '' ? assignmentData.user.profile_image : user_image}
                          className="rounded-circle mb-4"
                          alt="Profile"
                          style={{ width: 140, height: 140, objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.src = user_image;
                          }}
                        />
                        <h1 className="card-title mb-2" style={{ fontWeight: 700, fontSize: 36, color: '#1F2937', lineHeight: 1.2 }}>{userName}</h1>
                        <p className="text-muted mb-0" style={{ fontSize: 18, color: '#6B7280', fontWeight: 500 }}>{assignmentData?.test?.industry || ''}</p>
                        {assignmentData && (
                          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #E5E7EB', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 600 }}>Overall Score</span>
                              <Tooltip
                                title="Your Overall Score is weighted by question difficulty. Harder questions are worth more points, so this number may differ from the percentage of questions you answered correctly."
                                placement="top"
                              >
                                <span aria-label="How is this score calculated?" style={{ cursor: 'help', color: '#6B7280', fontSize: 13, lineHeight: 1 }}>ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 32, color: '#0051FF', lineHeight: 1.1 }}>{assignmentData.score}%</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Middle Column - Info Cards */}
                  <div className="col-3">
                    <div className="d-flex flex-column h-100">


                      {/* Test Date Card */}
                      <div className="card mb-3" style={{ borderRadius: 18, padding: '20px 24px', minHeight: 115, flex: 1 }}>
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ gap: 8 }}>
                          <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 5.66667H15M4.11111 1V2.55556M11.8889 1V2.55556M3.33333 8.77778H4.88889M3.33333 11.8889H4.88889M7.22222 8.77778H8.77778M7.22222 11.8889H8.77778M11.1111 8.77778H12.6667M11.1111 11.8889H12.6667M3.48889 15H12.5111C13.3823 15 13.8179 15 14.1507 14.8304C14.4433 14.6813 14.6813 14.4433 14.8304 14.1507C15 13.8179 15 13.3823 15 12.5111V5.04444C15 4.17325 15 3.73765 14.8304 3.4049C14.6813 3.1122 14.4433 2.87423 14.1507 2.7251C13.8179 2.55556 13.3823 2.55556 12.5111 2.55556H3.48889C2.6177 2.55556 2.1821 2.55556 1.84935 2.7251C1.55665 2.87423 1.31868 3.1122 1.16955 3.4049C1 3.73765 1 4.17325 1 5.04444V12.5111C1 13.3823 1 13.8179 1.16955 14.1507C1.31868 14.4433 1.55665 14.6813 1.84935 14.8304C2.1821 15 2.61769 15 3.48889 15Z" stroke="#0051FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="d-flex flex-column align-items-center text-center" style={{ lineHeight: 1.2 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Assessment date</span>
                            <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{testDate}</span>
                          </div>
                        </div>
                      </div>

                      {/* Time Spent Card */}
                      <div className="card" style={{ borderRadius: 18, padding: '20px 24px', minHeight: 115, flex: 1 }}>
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ gap: 8 }}>
                          <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="28" height="28" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.0001 5.00033V10.0003L13.3334 11.667M18.3334 10.0003C18.3334 14.6027 14.6025 18.3337 10.0001 18.3337C5.39771 18.3337 1.66675 14.6027 1.66675 10.0003C1.66675 5.39795 5.39771 1.66699 10.0001 1.66699C14.6025 1.66699 18.3334 5.39795 18.3334 10.0003Z" stroke="#0051FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="d-flex flex-column align-items-center text-center" style={{ lineHeight: 1.2 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Time spent</span>
                            <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{timeSpent}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Test Overview Card */}
                  <div className="col-5">
                    <div className="card h-100" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)', padding: '24px' }}>
                      <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                        <div className="test-overview-title" style={{ fontWeight: 700, fontSize: 20, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                          Assessment overview
                          <Tooltip
                            title="Shows the number of questions you answered correctly out of the total. This is a raw count and may differ from the skills score, which is weighted by question difficulty."
                            placement="top"
                          >
                            <span aria-label="What does this show?" style={{ cursor: 'help', color: '#6b7280', fontSize: 14, lineHeight: 1 }}>ⓘ</span>
                          </Tooltip>
                        </div>
                        <span style={{ color: '#6b7280', fontSize: 15, fontWeight: 600 }}>Questions</span>
                      </div>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        {/* Left side - Question count */}
                        <div className="d-flex align-items-baseline">
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 48, color: '#1f2937', marginRight: 8 }}>{assignmentData ? `${assignmentData.correct_answers}` : "--"}</span>
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 32, color: '#6b7280' }}>/{assignmentData ? assignmentData.total_questions : "--"}</span>
                        </div>
                        {/* Right side - Pie chart */}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                          <div style={{ width: 200, height: 200, minWidth: 200, minHeight: 200 }}>
                            <Pie data={pieChartData} options={pieChartOptions} />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#1890ff', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Correct</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({correctPercentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffa940', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Incorrect</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.total_questions - assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({incorrectPercentage.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280', fontStyle: 'italic', textAlign: 'right' }}>
                        Chart shows question counts, not weighted score.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second Row - Skills Table */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card p-4" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="test-skills-title" style={{ fontWeight: 700, fontSize: 20, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {assignmentData?.test?.industry ? (
                            <span>{assignmentData.test.industry} skills</span>
                          ) : (
                            'General skills'
                          )}
                          <Tooltip
                            title="Each skill percentage is weighted by question difficulty, not a share of questions answered."
                            placement="top"
                          >
                            <span aria-label="How are these scores calculated?" style={{ cursor: 'help', color: '#6b7280', fontSize: 14, lineHeight: 1 }}>ⓘ</span>
                          </Tooltip>
                        </div>
                        <a
                          onClick={(e) => { e.preventDefault(); setScoreInfoOpen(true); }}
                          href="#"
                          style={{ fontSize: 13, color: '#0051FF', fontWeight: 600, textDecoration: 'none' }}
                        >
                          How is my score calculated?
                        </a>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, fontStyle: 'italic' }}>
                        Percentages below are weighted by question difficulty, so they may not match the share of questions you answered correctly in each area.
                      </div>
                      <Table
                        columns={columns_Tests}
                        dataSource={rows_Tests}
                        rowKey="type"
                        pagination={false}
                        bordered={false}
                        loading={loading}
                        showHeader={true}
                        locale={{ emptyText: 'No skills available for this test.' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin view with authorization */}
      {accessAllowed === true && isAdmin && (
        <div className="test-result-container">
          <div className="content-header d-flex justify-content-between align-items-center">
            <h3><strong>"{assignmentData?.test.name}" results</strong></h3>
            <div className="d-flex gap-2">
              {isAdmin && assignmentData?.current_user_invited_candidate && (
                <button
                  className="btn btn-primary"
                  style={{ marginRight: '10px' }}
                  onClick={handleViewPDP}
                  disabled={!assignmentId}
                >
                  View PPP
                </button>
              )}
              <button
                className="btn btn-primary"
                style={{ marginRight: '10px' }}
                onClick={handleDownloadPDF}
                disabled={!assignmentId}
              >
                Download Assessment PDF
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDownloadCertificate}
                disabled={!assignmentId}
              >
                Download Certificate
              </button>
            </div>
          </div>

          {/* Anti-Cheat Warning */}
          {shouldShowAntiCheat && antiCheatReport && !antiCheatWarningDismissed && (
            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <AntiCheatWarning
                report={antiCheatReport}
                onViewDetails={handleViewAntiCheatDetails}
                onDismiss={handleDismissAntiCheatWarning}
              />
            </div>
          )}
          <div className="section-content layout" id="candidate-test-result-content" style={{
            margin: '0 auto',
          }}>
            <div className="mb-3">
              <div className="row mb-4 mt-3">
                {/* Profile Card - Always col-4 for non-owner view and candidate view */}
                <div className={`${isAdmin && assignmentData?.current_user_invited_candidate ? 'col-5' : 'col-4'}`}>
                  <div className="card h-100">
                    <div className="card-body d-flex flex-column justify-content-center align-items-center text-center" style={{ padding: '48px 24px' }}>
                      <img
                        src={assignmentData?.user?.profile_image && assignmentData.user.profile_image.trim() !== '' ? assignmentData.user.profile_image : user_image}
                        className="rounded-circle mb-4"
                        alt="Candidate"
                        style={{ width: 140, height: 140, objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.src = user_image;
                        }}
                      />
                      <h1 className="card-title mb-2" style={{ fontWeight: 700, fontSize: 36, color: '#1F2937', lineHeight: 1.2 }}>{userName}</h1>
                      <p className="text-muted mb-0" style={{ fontSize: 18, color: '#6B7280', fontWeight: 500 }}>{assignmentData?.test?.industry || ''}</p>
                      {assignmentData && (
                        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #E5E7EB', width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 600 }}>Overall Score</span>
                            <Tooltip
                              title="Overall Score is weighted by question difficulty. Harder questions are worth more points, so this number may differ from the percentage of questions answered correctly."
                              placement="top"
                            >
                              <span aria-label="How is this score calculated?" style={{ cursor: 'help', color: '#6B7280', fontSize: 13, lineHeight: 1 }}>ⓘ</span>
                            </Tooltip>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 32, color: '#0051FF', lineHeight: 1.1 }}>{assignmentData.score}%</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Middle Column - Info Cards for non-owner and candidate views */}
                {!(isAdmin && assignmentData?.current_user_invited_candidate) && (
                  <div className="col-3">
                    <div className="d-flex flex-column h-100">
                      {/* Achievement Badge Card - Only for admin/business view */}
                      {isAdmin && (
                        <div
                          className="card d-flex align-items-center justify-content-center mb-3"
                          style={{
                            borderRadius: 18,
                            padding: '20px 24px',
                            minHeight: 115,
                            backgroundColor: achievementBadge.bgColor,
                            border: `2px solid ${achievementBadge.borderColor}20`,
                            flex: 1
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {achievementBadge.icon}
                            <span style={{
                              fontWeight: 700,
                              fontSize: 16,
                              color: achievementBadge.textColor,
                              marginLeft: 12
                            }}>
                              {achievementBadge.text}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Test Date Card */}
                      <div className="card mb-3" style={{ borderRadius: 18, padding: '20px 24px', minHeight: !isAdmin ? 115 : 100, flex: !isAdmin ? 1 : 2 }}>
                        {isAdmin ? (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100" style={{ gap: 16 }}>
                            <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 5.66667H15M4.11111 1V2.55556M11.8889 1V2.55556M3.33333 8.77778H4.88889M3.33333 11.8889H4.88889M7.22222 8.77778H8.77778M7.22222 11.8889H8.77778M11.1111 8.77778H12.6667M11.1111 11.8889H12.6667M3.48889 15H12.5111C13.3823 15 13.8179 15 14.1507 14.8304C14.4433 14.6813 14.6813 14.4433 14.8304 14.1507C15 13.8179 15 13.3823 15 12.5111V5.04444C15 4.17325 15 3.73765 14.8304 3.4049C14.6813 3.1122 14.4433 2.87423 14.1507 2.7251C13.8179 2.55556 13.3823 2.55556 12.5111 2.55556H3.48889C2.6177 2.55556 2.1821 2.55556 1.84935 2.7251C1.55665 2.87423 1.31868 3.1122 1.16955 3.4049C1 3.73765 1 4.17325 1 5.04444V12.5111C1 13.3823 1 13.8179 1.16955 14.1507C1.31868 14.4433 1.55665 14.6813 1.84935 14.8304C2.1821 15 2.61769 15 3.48889 15Z" stroke="#0051FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="d-flex flex-column align-items-start" style={{ lineHeight: 1.2 }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Assessment date</span>
                              <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{testDate}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ gap: 8 }}>
                            <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 5.66667H15M4.11111 1V2.55556M11.8889 1V2.55556M3.33333 8.77778H4.88889M3.33333 11.8889H4.88889M7.22222 8.77778H8.77778M7.22222 11.8889H8.77778M11.1111 8.77778H12.6667M11.1111 11.8889H12.6667M3.48889 15H12.5111C13.3823 15 13.8179 15 14.1507 14.8304C14.4433 14.6813 14.6813 14.4433 14.8304 14.1507C15 13.8179 15 13.3823 15 12.5111V5.04444C15 4.17325 15 3.73765 14.8304 3.4049C14.6813 3.1122 14.4433 2.87423 14.1507 2.7251C13.8179 2.55556 13.3823 2.55556 12.5111 2.55556H3.48889C2.6177 2.55556 2.1821 2.55556 1.84935 2.7251C1.55665 2.87423 1.31868 3.1122 1.16955 3.4049C1 3.73765 1 4.17325 1 5.04444V12.5111C1 13.3823 1 13.8179 1.16955 14.1507C1.31868 14.4433 1.55665 14.6813 1.84935 14.8304C2.1821 15 2.61769 15 3.48889 15Z" stroke="#0051FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="d-flex flex-column align-items-center text-center" style={{ lineHeight: 1.2 }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Assessment date</span>
                              <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{testDate}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time Spent Card */}
                      <div className="card" style={{ borderRadius: 18, padding: '20px 24px', minHeight: !isAdmin ? 115 : 100, flex: !isAdmin ? 1 : 2 }}>
                        {isAdmin ? (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100" style={{ gap: 16 }}>
                            <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="28" height="28" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.0001 5.00033V10.0003L13.3334 11.667M18.3334 10.0003C18.3334 14.6027 14.6025 18.3337 10.0001 18.3337C5.39771 18.3337 1.66675 14.6027 1.66675 10.0003C1.66675 5.39795 5.39771 1.66699 10.0001 1.66699C14.6025 1.66699 18.3334 5.39795 18.3334 10.0003Z" stroke="#0051FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="d-flex flex-column align-items-start" style={{ lineHeight: 1.2 }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Time spent</span>
                              <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{timeSpent}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100" style={{ gap: 8 }}>
                            <div style={{ background: '#F2F7FF', borderRadius: 25, width: 51, height: 51, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="28" height="28" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.0001 5.00033V10.0003L13.3334 11.667M18.3334 10.0003C18.3334 14.6027 14.6025 18.3337 10.0001 18.3337C5.39771 18.3337 1.66675 14.6027 1.66675 10.0003C1.66675 5.39795 5.39771 1.66699 10.0001 1.66699C14.6025 1.66699 18.3334 5.39795 18.3334 10.0003Z" stroke="#0051FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <div className="d-flex flex-column align-items-center text-center" style={{ lineHeight: 1.2 }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: '#0051FF', marginBottom: 4 }}>Time spent</span>
                              <span style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>{timeSpent}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Right Column - Test Overview Card for non-owner and candidate views */}
                {!(isAdmin && assignmentData?.current_user_invited_candidate) && (
                  <div className="col-5">
                    <div className="card h-100" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)', padding: '24px' }}>
                      <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                        <div className="test-overview-title" style={{ fontWeight: 700, fontSize: 20, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                          Assessment overview
                          <Tooltip
                            title="Shows the number of questions you answered correctly out of the total. This is a raw count and may differ from the skills score, which is weighted by question difficulty."
                            placement="top"
                          >
                            <span aria-label="What does this show?" style={{ cursor: 'help', color: '#6b7280', fontSize: 14, lineHeight: 1 }}>ⓘ</span>
                          </Tooltip>
                        </div>
                        <span style={{ color: '#6b7280', fontSize: 15, fontWeight: 600 }}>Questions</span>
                      </div>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        {/* Left side - Question count */}
                        <div className="d-flex align-items-baseline">
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 48, color: '#1f2937', marginRight: 8 }}>{assignmentData ? `${assignmentData.correct_answers}` : "--"}</span>
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 32, color: '#6b7280' }}>/{assignmentData ? assignmentData.total_questions : "--"}</span>
                        </div>
                        {/* Right side - Pie chart */}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                          <div style={{ width: 200, height: 200, minWidth: 200, minHeight: 200 }}>
                            <Pie data={pieChartData} options={pieChartOptions} />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#1890ff', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Correct</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({correctPercentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffa940', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Incorrect</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.total_questions - assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({incorrectPercentage.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280', fontStyle: 'italic', textAlign: 'right' }}>
                        Chart shows question counts, not weighted score.
                      </div>
                    </div>
                  </div>
                )}                {/* Value Calculator Card - Only show to admin who invited the candidate */}
                {isAdmin && assignmentData?.current_user_invited_candidate && (
                  <div className="col-7">
                    <div className="card h-100" style={{ borderRadius: 20, boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)', border: '1px solid #E6F0FF', background: '#fff' }}>
                      <div className="card-body p-4" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        {/* Header with icon and title */}
                        <div className="d-flex align-items-center mb-3">
                          <div style={{ background: '#D6F3FF', borderRadius: 10, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 16 }}>
                            {/* SVG Icon */}
                            <svg width="28" height="24" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21.8689 16.7263V7.71973L20.4178 8.44526V16.7253L19.4502 19.2133H22.836L21.8689 16.7263Z" fill="#0051FF" />
                              <path d="M9.92898 12.3906L3.97192 9.41215V12.3848C3.97192 14.3883 7.22028 16.0124 11.2272 16.0124C15.2343 16.0124 18.4825 14.3883 18.4825 12.3848V9.41211L12.525 12.3907C11.5053 12.7947 10.9488 12.7945 9.92898 12.3906Z" fill="#0051FF" />
                              <path d="M11.8761 11.0934L22.1867 5.93812C22.5437 5.75963 22.5437 5.46766 22.1867 5.28915L11.8761 0.133867C11.5193 -0.0446222 10.9352 -0.0446222 10.5782 0.133867L0.267642 5.28915C-0.0892141 5.46763 -0.0892141 5.75961 0.267642 5.93812L10.5782 11.0934C10.9352 11.2719 11.5193 11.2719 11.8761 11.0934Z" fill="#0051FF" />
                            </svg>
                          </div>
                          <div>
                            <div style={{ color: '#7B8EC8', fontWeight: 400, fontSize: 16, lineHeight: 1 }}>TestInvest</div>
                            <div className="test-value-calculator-title" style={{ color: '#1F2937', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>Value Calculator</div>
                          </div>
                        </div>
                        {/* Main value and description */}
                        <div className="d-flex align-items-center mb-3" style={{ border: '1.5px solid #E6F0FF', borderRadius: 14, padding: '18px 24px', background: '#F9FBFF' }}>
                          {correctPercentage >= 95 ? (
                            // Perfect/Near-perfect score message
                            <>
                              <div style={{ color: '#059669', fontWeight: 600, fontSize: 28, marginRight: 24, display: 'flex', alignItems: 'center' }}>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 12 }}>
                                  <circle cx="16" cy="16" r="16" fill="#10B981" />
                                  <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Excellent!
                              </div>
                              <div style={{ color: '#059669', fontWeight: 600, fontSize: 15, lineHeight: 1.4 }}>
                                This candidate is already performing at gold star level.<br />
                                <span style={{ fontWeight: 500, fontSize: 13 }}>Minimal to no training investment required.</span>
                              </div>
                            </>
                          ) : hourlyRate === 0 ? (
                            // No hourly rate entered
                            <>
                              <div style={{ color: '#6B7280', fontWeight: 600, fontSize: 24, marginRight: 24 }}>
                                Enter hourly rate →
                              </div>
                              <div style={{ color: '#6B7280', fontWeight: 600, fontSize: 15, lineHeight: 1.4 }}>
                                Add your training cost per hour to see the<br />
                                total investment needed for this candidate.
                              </div>
                            </>
                          ) : (
                            // Normal calculation display
                            <>
                              <div style={{ color: '#1F2937', fontWeight: 600, fontSize: 32, marginRight: 32 }}>
                                {formatCurrency(trainingCost)}
                              </div>
                              <div style={{ color: '#1F2937', fontWeight: 700, fontSize: 15 }}>Estimate training and time investment to be a gold star hire</div>
                            </>
                          )}
                        </div>
                        {/* Details row */}
                        <div className="d-flex align-items-end justify-content-between mb-3" style={{ gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#ACACAC', fontWeight: 600, fontSize: 12, marginBottom: 2 }}>Est time + base hourly rate</div>
                            <div style={{ color: '#1F2937', fontWeight: 700, fontSize: 14 }}>
                              {correctPercentage >= 95 ? (
                                <span style={{ color: '#059669' }}>0-2 hours maintenance @ {hourlyRate > 0 ? formatCurrency(hourlyRate) : '$--'}/hour</span>
                              ) : (
                                `${trainingHours} hours upskilling @ ${hourlyRate > 0 ? formatCurrency(hourlyRate) : '$--'}/hour`
                              )}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#ACACAC', fontWeight: 600, fontSize: 12, marginBottom: 2 }}>
                              {hourlyRate === 0 ? 'Enter the base rate' : 'Training rate'}
                            </div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              background: hourlyRate === 0 ? '#FEF3C7' : 'transparent',
                              borderRadius: hourlyRate === 0 ? 8 : 0,
                              padding: hourlyRate === 0 ? '6px 8px' : '0',
                              border: hourlyRate === 0 ? '2px solid #F59E0B' : 'none',
                              transition: 'all 0.3s ease'
                            }}>
                              <span style={{ color: '#1F2937', fontWeight: 700, fontSize: 14, marginRight: 4 }}>$</span>
                              <input
                                type="number"
                                placeholder="50"
                                value={hourlyRate || ''}
                                onChange={handleHourlyRateChange}
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent',
                                  color: '#1F2937',
                                  fontWeight: 700,
                                  fontSize: 14,
                                  width: 50,
                                  padding: 0
                                }}
                              />
                              <span style={{ color: '#1F2937', fontWeight: 700, fontSize: 14, marginLeft: 2 }}>/hour</span>
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#ACACAC', fontWeight: 600, fontSize: 12, marginBottom: 2 }}>Assessment</div>
                            <div style={{ color: '#1F2937', fontWeight: 700, fontSize: 14 }}>
                              {assignmentData?.test?.name || 'Test Assessment'}
                            </div>
                          </div>
                        </div>
                        {/* Button row / Help section */}
                        {hourlyRate === 0 && correctPercentage < 95 && (
                          <div style={{
                            background: '#F0F9FF',
                            border: '1px solid #0EA5E9',
                            borderRadius: 12,
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="8" cy="8" r="8" fill="#0EA5E9" />
                              <path d="M8 4V8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                              <circle cx="8" cy="11" r="1" fill="white" />
                            </svg>
                            <span style={{ fontSize: 12, color: '#0369A1', fontWeight: 500 }}>
                              Enter the role hourly rate to calculate the total investment needed to upskill this candidate.
                            </span>
                          </div>
                        )}
                        {correctPercentage >= 95 && (
                          <div style={{
                            background: '#ECFDF5',
                            border: '1px solid #10B981',
                            borderRadius: 12,
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="8" cy="8" r="8" fill="#10B981" />
                              <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span style={{ fontSize: 12, color: '#047857', fontWeight: 500 }}>
                              🎉 This candidate is ready to contribute immediately with minimal onboarding time.
                            </span>
                          </div>
                        )}
                        {/* <div className="d-flex justify-content-end">
                          <button className="btn no-pdf" style={{ background: '#0051FF', color: '#fff', borderRadius: 32, fontWeight: 600, fontSize: 14, padding: '12px 24px', boxShadow: '0 2px 8px 0 rgba(0,81,255,0.10)' }}>View training partners</button>
                        </div> */}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Second Row - Only for Owner View */}
              {isAdmin && assignmentData?.current_user_invited_candidate && (
                <div className="row mb-4">
                  {/* Left Column - Info Cards and Test Overview */}
                  <div className="col-6">
                    {/* Info cards row */}
                    <div className="d-flex gap-3 mb-3">
                      <div
                        className="card flex-fill d-flex align-items-center justify-content-center"
                        style={{
                          borderRadius: 18,
                          padding: '16px 20px',
                          minHeight: 80,
                          backgroundColor: achievementBadge.bgColor,
                          border: `2px solid ${achievementBadge.borderColor}20`
                        }}
                      >
                        <div className="d-flex align-items-center">
                          {achievementBadge.icon}
                          <span style={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: achievementBadge.textColor,
                            marginLeft: 12
                          }}>
                            {achievementBadge.text}
                          </span>
                        </div>
                      </div>

                      <div className="card flex-fill d-flex align-items-center justify-content-center" style={{ borderRadius: 18, padding: '16px 20px', minHeight: 80 }}>
                        <div className="d-flex align-items-center w-100" style={{ gap: 14 }}>
                          <div style={{ background: '#F2F7FF', borderRadius: 20, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 5.66667H15M4.11111 1V2.55556M11.8889 1V2.55556M3.33333 8.77778H4.88889M3.33333 11.8889H4.88889M7.22222 8.77778H8.77778M7.22222 11.8889H8.77778M11.1111 8.77778H12.6667M11.1111 11.8889H12.6667M3.48889 15H12.5111C13.3823 15 13.8179 15 14.1507 14.8304C14.4433 14.6813 14.6813 14.4433 14.8304 14.1507C15 13.8179 15 13.3823 15 12.5111V5.04444C15 4.17325 15 3.73765 14.8304 3.4049C14.6813 3.1122 14.4433 2.87423 14.1507 2.7251C13.8179 2.55556 13.3823 2.55556 12.5111 2.55556H3.48889C2.6177 2.55556 2.1821 2.55556 1.84935 2.7251C1.55665 2.87423 1.31868 3.1122 1.16955 3.4049C1 3.73765 1 4.17325 1 5.04444V12.5111C1 13.3823 1 13.8179 1.16955 14.1507C1.31868 14.4433 1.55665 14.6813 1.84935 14.8304C2.1821 15 2.61769 15 3.48889 15Z" stroke="#0051FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="d-flex flex-column justify-content-center" style={{ lineHeight: 1.1 }}>
                            <span style={{ fontWeight: 600, fontSize: 12, color: '#0051FF', marginBottom: 2 }}>Assessment date</span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#1F2937' }}>{testDate}</span>
                          </div>
                        </div>
                      </div>

                      <div className="card flex-fill d-flex align-items-center justify-content-center" style={{ borderRadius: 18, padding: '16px 20px', minHeight: 80 }}>
                        <div className="d-flex align-items-center w-100" style={{ gap: 14 }}>
                          <div style={{ background: '#F2F7FF', borderRadius: 20, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.0001 5.00033V10.0003L13.3334 11.667M18.3334 10.0003C18.3334 14.6027 14.6025 18.3337 10.0001 18.3337C5.39771 18.3337 1.66675 14.6027 1.66675 10.0003C1.66675 5.39795 5.39771 1.66699 10.0001 1.66699C14.6025 1.66699 18.3334 5.39795 18.3334 10.0003Z" stroke="#0051FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="d-flex flex-column justify-content-center" style={{ lineHeight: 1.1 }}>
                            <span style={{ fontWeight: 600, fontSize: 12, color: '#0051FF', marginBottom: 2 }}>Time spent</span>
                            <span style={{ fontWeight: 700, fontSize: 16, color: '#1F2937' }}>{timeSpent}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Test Overview Card */}
                    <div className="card" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)', padding: '24px' }}>
                      <div className="d-flex w-100 justify-content-between align-items-center mb-3">
                        <div className="test-overview-title" style={{ fontWeight: 700, fontSize: 20, color: '#1f2937' }}>Assessment overview</div>
                        <span style={{ color: '#6b7280', fontSize: 15, fontWeight: 600 }}>Questions</span>
                      </div>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        {/* Left side - Question count */}
                        <div className="d-flex align-items-baseline">
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 48, color: '#1f2937', marginRight: 8 }}>{assignmentData ? `${assignmentData.correct_answers}` : "--"}</span>
                          <span className="test-score-numbers" style={{ fontWeight: 700, fontSize: 32, color: '#6b7280' }}>/{assignmentData ? assignmentData.total_questions : "--"}</span>
                        </div>
                        {/* Right side - Pie chart */}
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                          <div style={{ width: 200, height: 200, minWidth: 200, minHeight: 200 }}>
                            <Pie data={pieChartData} options={pieChartOptions} />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#1890ff', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Correct</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({correctPercentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffa940', borderRadius: '3px', marginRight: 8 }}></span>
                          <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 16 }}>Incorrect</span>
                          <span style={{ color: '#1f2937', fontWeight: 700, fontSize: 16 }}>
                            {assignmentData ? `${assignmentData.total_questions - assignmentData.correct_answers} of ${assignmentData.total_questions}` : '--'} ({incorrectPercentage.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 11, color: '#6b7280', fontStyle: 'italic', textAlign: 'right' }}>
                        Chart shows question counts, not weighted score.
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Skills Table */}
                  <div className="col-6">
                    <div className="card h-100 p-4" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)' }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="test-skills-title" style={{ fontWeight: 700, fontSize: 20, color: '#1F2937' }}>
                          {assignmentData?.test?.industry ? (
                            <span>{assignmentData.test.industry} skills</span>
                          ) : (
                            'General skills'
                          )}
                        </div>
                      </div>
                      <Table
                        columns={columns_Tests}
                        dataSource={rows_Tests}
                        rowKey="type"
                        pagination={false}
                        bordered={false}
                        loading={loading}
                        showHeader={true}
                        locale={{ emptyText: 'No skills available for this test.' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Skills Table for Non-Owner and Candidate Views */}
              {!(isAdmin && assignmentData?.current_user_invited_candidate) && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card p-4" style={{ borderRadius: 24, background: '#fff', boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.04)' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="test-skills-title" style={{ fontWeight: 700, fontSize: 20, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {assignmentData?.test?.industry ? (
                            <span>{assignmentData.test.industry} skills</span>
                          ) : (
                            'General skills'
                          )}
                          <Tooltip
                            title="Each skill percentage is weighted by question difficulty, not a share of questions answered."
                            placement="top"
                          >
                            <span aria-label="How are these scores calculated?" style={{ cursor: 'help', color: '#6b7280', fontSize: 14, lineHeight: 1 }}>ⓘ</span>
                          </Tooltip>
                        </div>
                        <a
                          onClick={(e) => { e.preventDefault(); setScoreInfoOpen(true); }}
                          href="#"
                          style={{ fontSize: 13, color: '#0051FF', fontWeight: 600, textDecoration: 'none' }}
                        >
                          How is my score calculated?
                        </a>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, fontStyle: 'italic' }}>
                        Percentages below are weighted by question difficulty, so they may not match the share of questions you answered correctly in each area.
                      </div>
                      <Table
                        columns={columns_Tests}
                        dataSource={rows_Tests}
                        rowKey="type"
                        pagination={false}
                        bordered={false}
                        loading={loading}
                        showHeader={true}
                        locale={{ emptyText: 'No skills available for this test.' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Anti-Cheat Modal */}
      {shouldShowAntiCheat && (
        <AntiCheatModal
          visible={antiCheatModalVisible}
          onClose={handleCloseAntiCheatModal}
          report={antiCheatReport}
          loading={antiCheatLoading}
        />
      )}

      {/* Score Explainer Modal */}
      <Modal
        open={scoreInfoOpen}
        onCancel={() => setScoreInfoOpen(false)}
        onOk={() => setScoreInfoOpen(false)}
        title="How is my score calculated?"
        okText="Got it"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={560}
      >
        <div style={{ fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
          <p style={{ marginBottom: 12 }}>
            Your score is <strong>weighted by question difficulty</strong>. Harder questions
            are worth more points than easier ones, so a correct answer on a hard
            question contributes more to your score than a correct answer on an easy one.
          </p>
          <p style={{ marginBottom: 12 }}>
            This is why you may see three different numbers on your report:
          </p>
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <li style={{ marginBottom: 6 }}>
              <strong>Questions answered correctly</strong> — a simple count (for example, 27 out of 40).
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong>Correct %</strong> — that count expressed as a percentage (for example, 67.5%).
            </li>
            <li style={{ marginBottom: 6 }}>
              <strong>Skill score %</strong> — the weighted result we actually use (for example, 72%).
              This is the number that best reflects your proficiency.
            </li>
          </ul>
          <div
            style={{
              background: '#F0F9FF',
              border: '1px solid #0EA5E9',
              borderLeft: '3px solid #0EA5E9',
              borderRadius: 6,
              padding: 12,
              fontSize: 13,
            }}
          >
            <strong>Quick example:</strong> a correct answer on a 3-point question counts the
            same as three correct answers on 1-point questions.
          </div>
        </div>
      </Modal>
    </>
  );
}

const BackIcon: React.CSSProperties = {
  position: "relative",
  top: -0.5,
  padding: 0
}

export default CandidateTestResult;
